import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

import { ApiError } from './errors'

/** Where the gateway lives. Empty in dev, where Vite proxies `/v1`. */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''

/** Returns a fresh access token, or null when the session is gone. */
export type TokenRefresher = () => Promise<string | null>

interface RequestConfig extends InternalAxiosRequestConfig {
  /** Set on login/refresh/exchange: a 401 there is the answer, not a
   *  reason to refresh and retry. */
  skipAuthRefresh?: boolean
  /** Guards against an endless retry if a refreshed token 401s again. */
  retried?: boolean
}

/**
 * The one axios instance every API module shares.
 *
 * Two behaviours here were paid for in bugs on the Flutter side and are
 * ported deliberately:
 *
 * 1. **The access token is held in memory only.** It is never written to
 *    localStorage — a tab reload re-obtains it from the http-only refresh
 *    cookie. (The admin UI in the api repo does persist it; that is the
 *    older decision and this replaces it.)
 * 2. **Refresh is single-flight.** The gateway rotates the refresh token on
 *    every call and treats a second call carrying the old cookie as token
 *    reuse, which revokes the whole session. Several requests 401-ing at
 *    once therefore share one refresh, and only the request that started it
 *    reports the session as expired.
 */
class ApiClientImpl {
  readonly axios: AxiosInstance
  private accessToken: string | null = null
  private refreshInFlight: Promise<string | null> | null = null
  private refresher: TokenRefresher | null = null
  private onSessionExpired: (() => void) | null = null
  /** A write attempted during an impersonated (read-only) session. One
   *  chokepoint for it because every request already passes through
   *  `handleError` — a caller does not have to check `error.code ===
   *  'impersonation_read_only'` itself to turn the refusal into a toast. */
  private onImpersonationBlocked: (() => void) | null = null
  /** Bumped whenever the token changes, so components that put the token in
   *  an image request header know to re-fetch. Mirrors `FilesApi.authVersion`. */
  private authVersion = 0
  private authVersionListeners = new Set<(version: number) => void>()

  constructor() {
    this.axios = axios.create({
      baseURL: API_BASE_URL,
      // No instance-wide Content-Type on purpose. axios sets
      // `application/json` for a plain-object body by itself, and leaves a
      // FormData body's header to the browser, which is the only thing that
      // can generate the multipart boundary. A default here wins over that
      // and every upload reaches the gateway as "request Content-Type isn't
      // multipart/form-data" — which reads like a bad file, not a bad header.
      // The refresh token is an http-only cookie; without this it never
      // travels and every reload logs the person out.
      withCredentials: true,
    })

    this.axios.interceptors.request.use((config) => {
      if (this.accessToken) {
        config.headers.Authorization = `Bearer ${this.accessToken}`
      }
      // Belt and braces for the same reason: anything that sets a JSON
      // content type on a multipart body breaks the boundary.
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        delete config.headers['Content-Type']
      }
      return config
    })

    this.axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => this.handleError(error),
    )
  }

  configure(options: {
    onRefresh: TokenRefresher
    onSessionExpired: () => void
    onImpersonationBlocked?: () => void
  }) {
    this.refresher = options.onRefresh
    this.onSessionExpired = options.onSessionExpired
    this.onImpersonationBlocked = options.onImpersonationBlocked ?? null
  }

  getAccessToken() {
    return this.accessToken
  }

  setAccessToken(token: string | null) {
    this.accessToken = token
    this.authVersion += 1
    for (const listener of this.authVersionListeners) listener(this.authVersion)
  }

  /** The Authorization header as a plain object, for an <img> fetch or a
   *  download that cannot go through axios. Empty when signed out. */
  authHeaders(): Record<string, string> {
    return this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}
  }

  onAuthVersionChange(listener: (version: number) => void) {
    this.authVersionListeners.add(listener)
    return () => this.authVersionListeners.delete(listener)
  }

  private async handleError(error: AxiosError): Promise<never> {
    const config = error.config as RequestConfig | undefined

    // The public engine refuses every non-GET while impersonating with this
    // exact code (`internal/auth/oauth` — the impersonation-read-only
    // middleware). It is not a failure a screen should render as an error;
    // `AuthProvider` turns it into "In der Nutzeransicht kann nichts
    // geändert werden." instead.
    const data = error.response?.data as Record<string, unknown> | undefined
    if (error.response?.status === 403 && data?.error === 'impersonation_read_only') {
      this.onImpersonationBlocked?.()
    }

    const canRetry =
      error.response?.status === 401 &&
      config != null &&
      !config.skipAuthRefresh &&
      !config.retried &&
      this.refresher != null

    if (!canRetry) throw ApiError.from(error)

    const isInitiator = this.refreshInFlight === null
    if (isInitiator) {
      this.refreshInFlight = this.refresher!()
        .catch(() => null)
        .finally(() => {
          this.refreshInFlight = null
        })
    }
    const token = await this.refreshInFlight!

    if (token) {
      const retry: RequestConfig = { ...(config as RequestConfig), retried: true }
      retry.headers.Authorization = `Bearer ${token}`
      try {
        // Rejecting with the retry's own response keeps the error the caller
        // sees about the request they made, not about the refresh.
        return (await this.axios.request(retry)) as never
      } catch (retryError) {
        throw ApiError.from(retryError)
      }
    }

    // Only the request that ran the refresh announces the expiry, so N
    // parallel 401s do not produce N redirects to the login screen.
    if (isInitiator) this.onSessionExpired?.()
    throw new ApiError({ status: 401, message: 'Sitzung abgelaufen', code: 'session_expired' })
  }

  /** Test-only: forget the token and the configured callbacks. */
  resetForTests() {
    this.accessToken = null
    this.refreshInFlight = null
    this.refresher = null
    this.onSessionExpired = null
    this.onImpersonationBlocked = null
  }
}

export const apiClient = new ApiClientImpl()

/** Requests that must not trigger a refresh-and-retry. */
export const skipAuthRefresh = { skipAuthRefresh: true } as Partial<RequestConfig>
